import {createContext, type PropsWithChildren, useContext, useMemo, useRef} from 'react';
import noop from '@tinkoff/utils/function/noop';
import throttle from 'lodash/throttle';

import {EventEmitter} from 'mattermost-redux/utils/event_emitter';

export type VirtualizedContextType = {
    scrollToBottom: () => void;
    addScrollListener: (cb: () => unknown) => void;
    removeScrollListener: (cb: () => unknown) => void;
    setScrollableListRef: (node: HTMLElement) => void;
    scrollListPage: (textbox: HTMLElement, direction: 'up' | 'down') => void;
};

export const VirtualizedContext = createContext<VirtualizedContextType>({
    scrollToBottom: noop,
    addScrollListener: noop,
    removeScrollListener: noop,
    setScrollableListRef: noop,
    scrollListPage: noop,
});

const SCROLL_TO_BOTTOM_EVENT = 'scroll-to-bottom';

const scrollEventEmitter = new EventEmitter();

const scrollToBottom = () => {
    scrollEventEmitter.emit(SCROLL_TO_BOTTOM_EVENT);
};

const addScrollListener = (cb: () => unknown) => {
    scrollEventEmitter.on(SCROLL_TO_BOTTOM_EVENT, cb);
};

const removeScrollListener = (cb: () => unknown) => {
    scrollEventEmitter.off(SCROLL_TO_BOTTOM_EVENT, cb);
};

export const VirtualizedContextProvider = ({children}: PropsWithChildren) => {
    const scrollableListRef = useRef<HTMLElement | null>(null);

    const contextValue: VirtualizedContextType = useMemo(() => {
        const scrollListPage = throttle((textbox: HTMLElement, direction: 'up' | 'down') => {
            if (!scrollableListRef.current) {
                return;
            }

            if (direction === 'down' && (textbox.scrollTop + textbox.clientHeight < textbox.scrollHeight)) {
                return;
            }

            if (direction === 'up' && textbox.scrollTop > 0) {
                return;
            }

            const offset = 60; // прокручиваем на чуть меньшую высоту
            const delta = scrollableListRef.current.clientHeight - offset;
            scrollableListRef.current.scrollBy({top: direction === 'down' ? delta : -delta, behavior: 'smooth'});
        }, 300, {trailing: false});

        return {
            scrollToBottom,
            addScrollListener,
            removeScrollListener,
            scrollListPage,
            setScrollableListRef: (node: HTMLElement) => {
                scrollableListRef.current = node;
            },
        };
    }, []);

    return (
        <VirtualizedContext.Provider value={contextValue}>
            {children}
        </VirtualizedContext.Provider>
    );
};

export const useVirtualizedContext = () => useContext(VirtualizedContext);
